import { Price } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { LoadingSkeleton } from '@components';
import { Box, styled, SxProps } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { formatPrice, getTaxInformation } from '@utils/price.utils';

const PriceBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const CurrentPriceBox = styled(Box)`
  display: flex;
  gap: 4px;
`;

const GrossPriceBox = styled(Box)`
  display: inline-block;
  color: ${colors.secondary.darkBlueGrey};
  font-size: 16px;
`;

const CompactPriceBox = styled(Box)`
  display: flex;
  gap: 2;
  align-items: center;
  flex: 1;
  justify-content: end;
`;

const CompactPriceInfoBox = styled(Box)`
  color: ${colors.secondary.darkBlueGrey};
  display: flex;
  align-items: center;
  flex: 1;
  ${breakpoints.up('md')} {
    justify-content: end;
  }
`;

export type PriceProps = {
  isLoading?: boolean;
  isExpired?: boolean;
  grossPrice?: Price | null;
  discountPrice?: Price | null;
  compact?: boolean;
  sx?: SxProps;
};

export const PriceInfo = ({ isLoading, grossPrice, discountPrice, isExpired, sx }: PriceProps) => {
  const currentPrice = discountPrice ? discountPrice : grossPrice;
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  if (isExpired) {
    return (
      <Box sx={{ color: colors.secondary.darkBlueGrey }}>{t(TextCodes.Product.Cart.Expired)}</Box>
    );
  }
  if (!grossPrice?.amount) {
    return (
      <Box sx={{ color: colors.secondary.darkBlueGrey }}>
        {t(TextCodes.Product.Cart.PriceNotAvailable)}
      </Box>
    );
  }

  return (
    <PriceBox sx={sx}>
      {discountPrice ? (
        <GrossPriceBox>
          {t(TextCodes.Product.Cart.SuggestedPrice)} {formatPrice(grossPrice)}
        </GrossPriceBox>
      ) : null}
      <CurrentPriceBox
        className='current-price-box'
        sx={{
          flexDirection: 'column',
        }}
      >
        <Box className='your-price-wrapper'>
          {discountPrice ? (
            <>
              {t(TextCodes.Product.Cart.YourPrice)} {formatPrice(currentPrice)}
            </>
          ) : (
            formatPrice(currentPrice)
          )}
        </Box>
        <Box>{getTaxInformation(currentPrice)}</Box>
      </CurrentPriceBox>
    </PriceBox>
  );
};

export const CompactPriceInfo = ({
  isLoading,
  grossPrice,
  discountPrice,
  isExpired,
}: PriceProps) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isExpired) {
    return <CompactPriceInfoBox>{t(TextCodes.Product.Cart.Expired)}</CompactPriceInfoBox>;
  }

  if (!grossPrice?.amount) {
    return <CompactPriceInfoBox>{t(TextCodes.Product.Cart.PriceNotAvailable)}</CompactPriceInfoBox>;
  }

  return (
    <CompactPriceBox>
      <PriceBox>
        <Box
          sx={{
            fontWeight: 'bold',
            textAlign: {
              xs: 'left',
              md: 'right',
            },
          }}
        >
          {formatPrice(grossPrice ?? discountPrice)}{' '}
          <Box
            component='span'
            sx={{
              textTransform: 'lowercase',
            }}
          >
            {t(TextCodes.Product.Cart.SuggestedPrice)}
          </Box>{' '}
          {getTaxInformation(grossPrice ?? discountPrice)}
        </Box>
      </PriceBox>
    </CompactPriceBox>
  );
};
